import { z } from 'zod';
import { CompanyProfileInputSchema } from './company';
import { BusinessCodeSchema } from './identifiers';
import { LocalizedLabelSchema } from './localized';
import { CreateLegalEntitySchema } from './organization';

/**
 * The file a new client deployment is initialized from (OPS-005, ADR-0027).
 *
 * One JSON file per client, prepared from the client data-intake checklist
 * (`docs/operations/client-data-intake-checklist.md`) and kept with the deployment's records. A file
 * holding registration, tax, address or contact details never enters this repository; a display-only
 * file (public names, country, currency, timezone, logo) may live under `deploy/clients/<client>/`
 * (ADR-0038). It carries only what the product cannot run without: the company profile and the
 * organization's legal entities and branches. Everything else — roles, approval thresholds, reference
 * lists, numbering formats — is configured afterwards through the product, so each change is audited.
 */
export const ClientInitBranchSchema = z.strictObject({
  code: BusinessCodeSchema,
  name: LocalizedLabelSchema,
  city: LocalizedLabelSchema,
  costCenterCode: BusinessCodeSchema.optional(),
});

/** A file next to the client file (no directories, no traversal), e.g. `logo.jpg`. */
const SiblingFileSchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/, { message: 'SIBLING_FILE_NAME_EXPECTED' })
  .refine((name) => !name.includes('..'), { message: 'SIBLING_FILE_NAME_EXPECTED' });

export const ClientInitFileSchema = z
  .strictObject({
    schemaVersion: z.literal(1),
    company: CompanyProfileInputSchema,
    /**
     * The client's own images, read from beside this file and uploaded through the same validation as
     * the screen (PNG/JPEG by magic bytes, size limit). Uploaded only into a slot that has **never**
     * held an image, so an administrator's later replacement or removal is never undone by a re-run.
     */
    brandAssets: z.strictObject({ logo: SiblingFileSchema.optional() }).optional(),
    /**
     * Legal entities and branches. May be empty until the client confirms its legal record; the
     * administrator then adds them on the Organization screen.
     */
    legalEntities: z
      .array(CreateLegalEntitySchema.extend({ branches: z.array(ClientInitBranchSchema).max(200) }))
      .max(20)
      .default([]),
  })
  .refine(
    (file) =>
      new Set(file.legalEntities.map((entity) => entity.code)).size === file.legalEntities.length,
    { message: 'DUPLICATE_LEGAL_ENTITY_CODE', path: ['legalEntities'] },
  )
  .refine(
    (file) =>
      file.legalEntities.every(
        (entity) =>
          new Set(entity.branches.map((branch) => branch.code)).size === entity.branches.length,
      ),
    { message: 'DUPLICATE_BRANCH_CODE', path: ['legalEntities'] },
  );
export type ClientInitFile = z.infer<typeof ClientInitFileSchema>;

/**
 * What happened to each item: created now, already present and matching, present and different, or
 * skipped because an administrator has already managed it (e.g. a logo replaced or removed).
 */
export const CLIENT_INIT_OUTCOMES = ['created', 'exists', 'differs', 'skipped'] as const;
export type ClientInitOutcome = (typeof CLIENT_INIT_OUTCOMES)[number];

export interface ClientInitStep {
  item: string;
  outcome: ClientInitOutcome;
  /** For `differs`: which fields do not match. Nothing is overwritten. */
  fields?: string[];
}
